import { useMemo, useState } from 'react';
import { is4xxError } from '@/services/api-base';
import { useGetShareRecipientsQuery, type ShareRecipient } from '../services/shareApi';

/** asset-service's limit on one team send. */
export const MAX_TEAM_RECIPIENTS = 100;

/** A teammate as the picker shows them. */
export interface TeamMember extends ShareRecipient {
  /** "First Last", or the email when the profile has no name. */
  name: string;
}

export const memberName = (recipient: ShareRecipient): string => {
  const parts = [recipient.firstName, recipient.lastName]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '');
  return parts.length > 0 ? parts.join(' ') : recipient.email;
};

const matchesQuery = (member: TeamMember, query: string): boolean =>
  `${member.name} ${member.email}`.toLowerCase().includes(query.toLowerCase());

/**
 * The team list's read:
 *  - `loading`: no list yet.
 *  - `failed`: the last read failed, even over a list from an earlier one,
 *    which is never offered as current. `retryable` is false for a 4xx
 *    (role, scope, unknown photoset, no account), which a retry cannot fix.
 *  - `ready`: a list, possibly empty.
 */
export type TeamListState =
  | { status: 'loading' }
  | { status: 'failed'; retryable: boolean }
  | { status: 'ready' };

export type InitialSelection = 'none' | 'all';

export interface TeamRecipients {
  list: TeamListState;
  /** The list is being read again over the one shown, which may be stale. */
  refreshing: boolean;
  /** Everyone on the list, in the order asset-service sorted them. */
  members: TeamMember[];
  /** The members matching `query`. */
  shown: TeamMember[];
  query: string;
  setQuery: (query: string) => void;
  /** The checked members, in list order. Only ever members on the list. */
  picked: TeamMember[];
  isPicked: (userId: string) => boolean;
  toggle: (userId: string) => void;
  /** Every member is checked (and there is at least one). */
  allPicked: boolean;
  /** Select All, or Clear All once everyone is checked. Spans the whole
   *  list, whatever the search shows. */
  toggleAll: () => void;
  /** Leave exactly these checked. */
  pick: (userIds: readonly string[]) => void;
  retry: () => void;
}

/**
 * The team a photoset can be emailed to, and who of them is checked
 * (DV-3715). Shared by Internal Share, where nobody starts checked, and Make
 * Visible & Notify (DV-3563), where everyone does.
 *
 * Read fresh each time the picker mounts. The checked set is kept as ids and
 * read against the current list, so a member who drops off it on a re-read
 * is never counted or sent.
 */
export const useTeamRecipients = (
  photosetId: string,
  initialSelection: InitialSelection = 'none'
): TeamRecipients => {
  const { currentData, error, isFetching, refetch } = useGetShareRecipientsQuery(photosetId, {
    refetchOnMountOrArgChange: true
  });
  const members = useMemo(
    () => (currentData ?? []).map((recipient) => ({ ...recipient, name: memberName(recipient) })),
    [currentData]
  );
  // Null until the sender changes the selection: the initial selection then
  // follows the list as it loads.
  const [chosen, setChosen] = useState<ReadonlySet<string> | null>(null);
  const [query, setQuery] = useState('');

  const checked =
    chosen ?? new Set(initialSelection === 'all' ? members.map((member) => member.userId) : []);
  const picked = members.filter((member) => checked.has(member.userId));
  const pickedIds = new Set(picked.map((member) => member.userId));
  const allPicked = members.length > 0 && picked.length === members.length;
  const trimmedQuery = query.trim();
  const shown =
    trimmedQuery === '' ? members : members.filter((member) => matchesQuery(member, trimmedQuery));

  const toggle = (userId: string) => {
    const next = new Set(pickedIds);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setChosen(next);
  };

  return {
    list: listState(currentData !== undefined, isFetching, error),
    refreshing: currentData !== undefined && isFetching,
    members,
    shown,
    query,
    setQuery,
    picked,
    isPicked: (userId) => pickedIds.has(userId),
    toggle,
    allPicked,
    toggleAll: () =>
      setChosen(new Set(allPicked ? [] : members.map((member) => member.userId))),
    pick: (userIds) => setChosen(new Set(userIds)),
    retry: () => void refetch()
  };
};

const listState = (hasList: boolean, fetching: boolean, error: unknown): TeamListState => {
  // RTK keeps `currentData` through a failed re-read (and clears `error` only
  // on a success): after a 400's re-read fails, the list held is the one
  // asset-service just refused.
  if (error !== undefined && !fetching) return { status: 'failed', retryable: !is4xxError(error) };
  return hasList ? { status: 'ready' } : { status: 'loading' };
};
